import { ReactNode } from 'react';
import styled from 'styled-components';
import { MarginProps, space } from 'styled-system';

import { Box } from 'components/Box';
import { Grid, GridItem } from 'components/Grid';
import { ListingCounts } from 'components/Listing/ListingCounts';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import {
  getContentFirstRealBlockIndex,
  getContentHasBodyHeader,
  getContentListingCounts,
  getContentShowListingCounts,
  getIsContentFullWidthLayout,
} from 'modules/cms/helpers';
import { mapCmsBlocksForBody } from 'modules/cms/mappers';
import { useCurrentUserIsStaff } from 'modules/main/store/hooks/useCurrentUserIsStaff';
import { gutterWidth, maxWidth, sectionPadding } from 'theme';

import { CmsBodyHeader } from './CmsBodyHeader';
import { CmsBodyMainContent } from './CmsBodyMainContent';
import { CmsBodyMainContentFooter } from './CmsBodyMainContentFooter';

const Header = styled.header`
  ${space};
`;

const Main = styled.article<MarginProps>`
  ${space};
`;

type Props = {
  belowBlocks: ReactNode | undefined;
  lowerSidebar: ReactNode;
  content: CmsApiPage;
  upperSidebar: ReactNode;
  headerImage: ReactNode | undefined;
  padBottom: boolean;
  padTop: boolean;
  socialButtons: ReactNode | null | undefined;
  subscribeButton: ReactNode | null | undefined;
};

export function CmsBody({
  belowBlocks,
  lowerSidebar,
  content,
  upperSidebar,
  headerImage,
  padBottom,
  padTop,
  socialButtons,
  subscribeButton,
}: Props) {
  const hasSidebar = lowerSidebar || upperSidebar;
  const { layout } = content;

  const isStaff = useCurrentUserIsStaff();

  const fullWidthLayout = getIsContentFullWidthLayout({ content });
  const showListingCounts = getContentShowListingCounts({ content });
  const listingCounts = getContentListingCounts({ content });
  const hasHeader = getContentHasBodyHeader({ content });
  const firstRealBlockIndex = getContentFirstRealBlockIndex({ content });

  const mappedContentBlocks = mapCmsBlocksForBody({ content });

  const firstRealBlock = mappedContentBlocks[firstRealBlockIndex];
  const firstRealBlockHasBackground = Boolean(
    mappedContentBlocks?.length && firstRealBlock?.hasBackground
  );

  return (
    <>
      {socialButtons && layout === 'NARROW' && !isStaff && !hasSidebar && (
        <Box
          position="sticky"
          zIndex={1}
          top={75}
          height={0}
          display={['none', null, null, 'flex']}
          justifyContent="flex-end"
          m="0 auto"
          maxWidth={maxWidth[3]}
        >
          <Box
            m={sectionPadding}
            display="flex"
            flexDirection="column"
            alignItems="center"
            width={gutterWidth + 32}
          >
            {socialButtons}
          </Box>
        </Box>
      )}

      <Box
        component="main"
        ml="auto"
        mr="auto"
        mb={!padBottom ? [-36, null, null, -54] : undefined}
        data-qa-id="content-container"
      >
        {(hasHeader || headerImage || showListingCounts) && (
          <Header>
            {hasHeader && (
              <Box m={sectionPadding}>
                <Grid>
                  <GridItem
                    columnStart={[
                      1,
                      1,
                      1,
                      fullWidthLayout || hasSidebar ? 1 : 2,
                    ]}
                    columnEnd={[
                      13,
                      13,
                      13,
                      fullWidthLayout || hasSidebar ? 13 : 12,
                    ]}
                  >
                    <Box>
                      <CmsBodyHeader content={content} />
                    </Box>
                  </GridItem>
                </Grid>
              </Box>
            )}

            {headerImage && (
              <Box m={sectionPadding}>
                <Grid>
                  <GridItem
                    columnStart={[
                      1,
                      1,
                      1,
                      fullWidthLayout || hasSidebar ? 1 : 2,
                    ]}
                    columnEnd={[
                      13,
                      13,
                      13,
                      fullWidthLayout || hasSidebar ? 13 : 12,
                    ]}
                  >
                    {headerImage}
                  </GridItem>
                </Grid>
              </Box>
            )}

            {showListingCounts && listingCounts ? (
              <ListingCounts
                darkMode={firstRealBlockHasBackground}
                listingCounts={listingCounts}
              />
            ) : null}
          </Header>
        )}

        <Main
          m={sectionPadding}
          mt={padTop || hasSidebar ? undefined : [0, 0, 0, 0]}
          mb={[0, 0, 0, 0]}
        >
          <CmsBodyMainContent
            content={content}
            mappedBlocks={mappedContentBlocks}
            upperSidebar={upperSidebar}
            lowerSidebar={lowerSidebar}
            footer={
              socialButtons || subscribeButton || belowBlocks ? (
                <CmsBodyMainContentFooter
                  belowBlocks={belowBlocks}
                  socialButtons={socialButtons}
                  subscribeButton={subscribeButton}
                />
              ) : undefined
            }
          />
        </Main>
      </Box>
    </>
  );
}
